import type { AuthenticationResult } from '../../services/AuthService';
import { logOut } from '../../store/reducers/AuthSlice';

const AUTH_STORAGE_KEYS = {
  local: 'persist:auth',
  session: 'persist:authSession',
} as const;

const MAX_CACHED_REFRESH_REQUESTS = 2;

type RefreshRequest = {
  readonly generation: number;
  readonly promise: Promise<AuthenticationResult>;
};

export type TokenRefresh = (
  refreshToken: string,
) => Promise<AuthenticationResult>;

type TerminateAuthSessionOptions = {
  readonly dispatch: (action: ReturnType<typeof logOut>) => void;
  readonly redirectToLogin: () => void;
};

const refreshRequests = new Map<string, RefreshRequest>();
let authSessionGeneration = 0;

export const getAuthSessionGeneration = () => authSessionGeneration;

export const invalidatePendingTokenRefreshes = () => {
  authSessionGeneration += 1;
  refreshRequests.clear();
};

export const getRefreshAuthentication = (
  refreshToken: string,
  refreshTokens: TokenRefresh,
  generation: number,
) => {
  const cachedRequest = refreshRequests.get(refreshToken);
  if (cachedRequest?.generation === generation) {
    return cachedRequest.promise;
  }

  const promise = refreshTokens(refreshToken);
  refreshRequests.set(refreshToken, { generation, promise });

  while (refreshRequests.size > MAX_CACHED_REFRESH_REQUESTS) {
    const oldestRefreshToken = refreshRequests.keys().next().value;
    if (oldestRefreshToken === undefined) break;
    refreshRequests.delete(oldestRefreshToken);
  }

  void promise.catch(() => {
    if (refreshRequests.get(refreshToken)?.promise === promise) {
      refreshRequests.delete(refreshToken);
    }
  });

  return promise;
};

export const terminateAuthSession = ({
  dispatch,
  redirectToLogin,
}: TerminateAuthSessionOptions) => {
  invalidatePendingTokenRefreshes();
  localStorage.removeItem(AUTH_STORAGE_KEYS.local);
  sessionStorage.removeItem(AUTH_STORAGE_KEYS.session);
  dispatch(logOut());
  redirectToLogin();
};
